import React, { useEffect } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { BACKEND_URL, FRONTEND_CNET_URL, RG } from '../../../utils/constants';
import { PAYMENT_MODE } from '../../../utils/payments';
import { getSessionId } from '../../../utils/gtm';
import fetch from '../../../utils/fetch';
import PropTypes from 'prop-types';
import { useFormState } from 'react-final-form';

const debug = require('debug')('cw:RevolutStripeForm');

export const cancelPaymentOnDrupal = (orderId, paymentId) => {
  fetch(`${BACKEND_URL}/commerce/payment/void/${orderId}/${paymentId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Helper function to complete payment after the order has been created.
export async function completePayment(
  stripe,
  elements,
  orderId,
  donationType,
  checkoutUrl,
  giftaid,
  amount,
  values,
  isTestSuite = false,
) {
  try {
    // Stripe says elements.submit() should be called as soon
    // as pay button is pressed, before any async work.
    elements.submit();

    // Create payment and receive client secret.
    const responseCreate = await fetch(`${BACKEND_URL}/commerce/payment/create/${orderId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        gateway: `decoupled_stripe_revolut_${donationType === RG ? 'rg_' : ''}${PAYMENT_MODE}`,
        type: 'credit_card',
      },
    });

    const paymentId = responseCreate.body.payment_id[0].value;
    const clientSecret = responseCreate.body.remote_id[0].value;

    // TODO: Passing order_id and payment_id is simplest solution
    //   but the proper one would be resolve it from payment intent ID and
    //   client secret, which are returned from Stripe.
    // Amount passed for setting it on checkout form in case of payment error.
    let returnUrl = checkoutUrl.includes('?')
      ? `${FRONTEND_CNET_URL}${checkoutUrl}&amount=${amount}&order_id=${orderId}&payment_id=${paymentId}&giftaid=${giftaid}`
      : `${FRONTEND_CNET_URL}${checkoutUrl}?amount=${amount}&order_id=${orderId}&payment_id=${paymentId}&giftaid=${giftaid}`;

    if (isTestSuite) {
      returnUrl += '&test-suite=true';
    }

    const sid = await getSessionId();

    // Pass the current GA session value.
    if (sid) {
      returnUrl += `&ga_custom_sid=${sid}`;
    }

    const countryCode = values.address.country.value;

    const params = {
      //`Elements` instance that was used to create the Setup Element
      elements,
      clientSecret,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: {
          billing_details: {
            email: values.email,
            name: values.first_name,
            phone: values.phone ?? '',
            address: {
              city: values.address.locality ?? '',
              country: countryCode,
              line1: values.address.addressLine1 ?? '',
              line2: values.address.addressLine2 ?? '',
              state:
                values.address.administrativeArea && values.address.administrativeArea[countryCode]
                  ? values.address.administrativeArea[countryCode].value ||
                    values.address.administrativeArea[countryCode]
                  : '',
              postal_code: values.address.postalCode ?? '',
            },
          },
        },
      },
    };

    const { error } =
      donationType === RG ? await stripe.confirmSetup(params) : await stripe.confirmPayment(params);

    if (error) {
      debug('Stripe.js could not handle payment/setup.', error);
      cancelPaymentOnDrupal(orderId, paymentId);
      return {
        type: 'error',
        errorMessage:
          'There has been an issue with your payment. Please re-check your payment details or check with your bank.',
      };
    }

    // In case of no errors the customer will be redirected to 'return_url'.
    // So nothing else can be executed here.
    return {
      type: 'success',
      orderId: orderId,
    };
  } catch (error) {
    debug('Could not process Stripe payment.', error);
    // Show message to the user.
    return {
      type: 'error',
      errorMessage: '',
    };
  }
}

export async function completeOrder(orderId, paymentId) {
  // We call this function only when return_url received successful result
  // from Stripe. So we redirect user to the thank you page in any case.
  let captureResponse = null;
  try {
    // Inform the backend about successful payment and finalize donation on Drupal.
    captureResponse = await fetch(
      `${BACKEND_URL}/commerce/payment/capture/${orderId}/${paymentId}`,
      {
        method: 'POST',
        // Requests with 409 status code won't be sent to Rollbar and the request
        // won't trigger errors.
        ignoreStatusCodes: [409],
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  } catch (error) {
    debug('Could not process Stripe payment.', error);
  }

  return {
    type: 'success',
    orderId: orderId,
    captureStatusCode: captureResponse ? captureResponse.statusCode : null,
  };
}

export const hasReturnUrlValuesInQuery = (query) =>
  query &&
  query.order_id &&
  query.payment_id &&
  query.redirect_status &&
  ((query.type === 'single' && query.payment_intent && query.payment_intent_client_secret) ||
    (query.type === 'monthly' && query.setup_intent && query.setup_intent_client_secret));

const RevolutStripeForm = ({ onCompletePayment, orderId = null }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { values } = useFormState();

  useEffect(() => {
    if (!orderId) {
      return;
    }
    if (onCompletePayment) {
      onCompletePayment(stripe, elements, values);
    }
  }, [orderId, stripe]);

  if (!stripe) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <PaymentElement />
    </>
  );
};

RevolutStripeForm.propTypes = {
  orderId: PropTypes.number,
  onCompletePayment: PropTypes.func.isRequired,
};

export default RevolutStripeForm;
